/**
 * @file     LocationScores.tsx
 * @purpose  Walk and bike accessibility indices (0 to 100, OSM network) and
 *           raw minutes to named destinations (supermarket, SkyTrain station,
 *           downtown) as hairline rows, with the source line. The index is
 *           computed and cached on first view when it is not routed yet.
 */
'use client';

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { hairline, hubColors } from '@/components/hub/hubKitStyles';
import { ListingIcon } from '@/components/icons/ListingIcons';
import { fontWeightTokens } from '@/design-system/tokens/typography';

export interface LocationScoreData {
  walkTimeS: number | null;
  transitTimeS: number | null;
  driveTimeS: number | null;
  bikeTimeS?: number | null;
  stationName?: string | null;
  walkPoiName?: string | null;
  driveDestination?: string | null;
  /** 0-100 amenity-accessibility indices (OSM network). Null until computed. */
  walkScore?: number | null;
  bikeScore?: number | null;
  /** Scoring provenance: 'ors' is terminal; anything else earns a routed upgrade. */
  scoreMethod?: string | null;
}

function formatTime(s: number): string {
  const m = Math.round(s / 60);
  if (m < 1) return '<1 min';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

interface Row {
  icon: React.ReactNode;
  duration: string;
  description: string;
}

function ScoreRow({ label, value }: { label: string; value: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, minHeight: 64, py: 3, borderBottom: hairline }}>
      <Box component="span" sx={{ fontSize: 15, fontWeight: fontWeightTokens.medium, color: hubColors.textPrimary }}>{label}</Box>
      <Box component="span" sx={{ fontSize: 28, fontWeight: fontWeightTokens.medium, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: hubColors.textPrimary }}>
        {value}
        <Box component="span" sx={{ ml: 1, fontSize: 13, color: hubColors.textMuted }}>/ 100</Box>
      </Box>
    </Box>
  );
}

interface Props {
  scores: LocationScoreData;
  /** When set, lazily computes and caches the 0-100 walk/bike index on first
   *  view if it is not stored yet (see /api/mls/access-score). */
  listingKey?: string | null;
}

export function LocationScores({ scores, listingKey }: Props) {
  const { t } = useTranslation();

  // First viewer pays one ORS round trip; the route caches it for everyone after.
  const [lazyScores, setLazyScores] = useState<{ walk: number | null; bike: number | null } | null>(null);
  // A haversine baseline (numbers already showing) still earns one routed
  // upgrade; an 'ors' row never re-fetches.
  const needsFetch = !!listingKey && scores.scoreMethod !== 'ors' && lazyScores == null;
  useEffect(() => {
    if (!needsFetch || !listingKey) return;
    let active = true;
    fetch(`/api/mls/access-score?key=${encodeURIComponent(listingKey)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { walkScore: number | null; bikeScore: number | null } | null) => {
        if (active && d && (d.walkScore != null || d.bikeScore != null)) {
          setLazyScores({ walk: d.walkScore, bike: d.bikeScore });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [needsFetch, listingKey]);

  // Routed (lazyScores) wins over the haversine baseline once it arrives.
  const walkScore = lazyScores?.walk ?? scores.walkScore ?? null;
  const bikeScore = lazyScores?.bike ?? scores.bikeScore ?? null;

  const stationLabel = scores.stationName ?? 'nearest SkyTrain station';
  const walkLabel = scores.walkPoiName ?? 'nearest supermarket';
  const driveLabel = scores.driveDestination ?? 'downtown Vancouver';

  const rows: Row[] = [
    scores.walkTimeS != null && { icon: <ListingIcon name="walk" size={18} />, duration: formatTime(scores.walkTimeS), description: `Walk to ${walkLabel}` },
    scores.bikeTimeS != null && { icon: <ListingIcon name="bike" size={18} />, duration: formatTime(scores.bikeTimeS), description: `Bike to ${stationLabel}` },
    scores.transitTimeS != null && { icon: <ListingIcon name="transit" size={18} />, duration: formatTime(scores.transitTimeS), description: `Walk to ${stationLabel}` },
    scores.driveTimeS != null && { icon: <ListingIcon name="car" size={18} />, duration: formatTime(scores.driveTimeS), description: `Drive to ${driveLabel}` },
  ].filter(Boolean) as Row[];

  if (rows.length === 0 && walkScore == null && bikeScore == null) return null;

  return (
    <Box>
      {walkScore != null ? <ScoreRow label={t('listingDetail.location.walkScore', { defaultValue: 'Walk score' })} value={walkScore} /> : null}
      {bikeScore != null ? <ScoreRow label={t('listingDetail.location.bikeScore', { defaultValue: 'Bike score' })} value={bikeScore} /> : null}
      {rows.map((row) => (
        <Box key={row.description} sx={{ display: 'flex', alignItems: 'center', gap: 3, minHeight: 48, py: 2, borderBottom: hairline }}>
          <Box sx={{ color: hubColors.textSecondary, lineHeight: 0, flexShrink: 0 }}>{row.icon}</Box>
          <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 15, color: hubColors.textSecondary }}>{row.description}</Box>
          <Box component="span" sx={{ flexShrink: 0, fontSize: 15, fontWeight: fontWeightTokens.medium, fontVariantNumeric: 'tabular-nums', color: hubColors.textPrimary }}>
            {row.duration}
          </Box>
        </Box>
      ))}
      <Box component="p" sx={{ m: 0, mt: 3, fontSize: 13, lineHeight: 1.5, color: hubColors.textSecondary }}>
        {t('listingDetail.location.source', { defaultValue: 'Estimated travel times. Map data © OpenStreetMap contributors.' })}
      </Box>
    </Box>
  );
}
